/**
 * ExtrasSection — Instructions step (Wave 4 native).
 *
 * Order-scoped extra-services picker. Renders one toggle CARD per instruction-
 * catalogue extra (FAST_DELIVERY, FAVOURITE_CREATIVE, …) — name + optional hint
 * on the left, emphasized price on the right — and reports toggles via
 * `onToggle`.
 *
 * ⚠️ Data-source fix (why the pre-revert version was reverted): the deleted
 * native section read `extras={catalogue?.extras ?? []}` — the v2 PRODUCT
 * catalogue's extras, which are **empty under v2** → the section showed nothing
 * → dogfood-08 revert. It now consumes the INSTRUCTION catalogue's
 * ProductKind-keyed `ResolvedInstructionOption` map from `useInstructionCatalogue`
 * (sourced from the v2 `…/instructions` DELIVERY flavor).
 *
 * ⚠️ Wire-compat (MODULE-CONTRACT §11.4): the page writes ProductKind STRINGS
 * (e.g. `'FAST_DELIVERY'`) into `instructionsSlice.extraServices`, NOT SF ids —
 * so this section keys everything off the option's `kind`, never its
 * `productCode` (even though v2 now hands us one; rekeying would break old
 * drafts without a `PF_VERSION` bump). The page owns both the `extraServices`
 * write and the `extrasFromInstructions` cart-total re-derive; this section is
 * presentational.
 */

import React from 'react'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { GRAY_700, GRAY_900, GREEN_600 } from 'constants/styling/theme'
import { PfInlineLoading } from '../../../components/PfInlineLoading.component'
import type { ResolvedInstructionOption } from 'models/purchaseFlowRevamp'
import type { ProductKind } from 'constants/product'

import { MUICheckCard } from 'components/common/MUICards'
import { SUBSCRIPTION_ONLY_EXTRA_KINDS, formatInstructionOptionPrice } from '../extras.helpers'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'

interface Props {
  /** ProductKind-keyed instruction-catalogue extras from `useInstructionCatalogue`. */
  extras: Partial<Record<ProductKind, ResolvedInstructionOption>>
  /** Catalogue currency (labels the price). */
  currency?: string
  /** Currently-selected ProductKind strings (`instructionsSlice.extraServices`). */
  selectedKinds: ReadonlyArray<string>
  onToggle: (kind: ProductKind, next: boolean) => void
  isLoading: boolean
  /**
   * The options call failed. Without this the empty bucket renders as "no extras
   * available" — a broken catalogue claiming, with confidence, that the client
   * cannot buy fast delivery.
   */
  hasError?: boolean
  /**
   * The workspace has a paid plan, so the plan-only extras are INCLUDED: shown
   * ticked, not unselectable, and priced as free rather than as an add-on
   * (legacy's `title_subscribed` / `free_badge` card).
   */
  subscriptionIncluded?: boolean
}

export const ExtrasSection: React.FC<Props> = ({ extras, currency, selectedKinds, onToggle, isLoading, hasError = false, subscriptionIncluded = false }) => {
  const { t } = useTranslation(['purchase_flow_revamp', 'fast_delivery_instruction_card'])
  const selectedSet = new Set(selectedKinds)
  const entries = Object.entries(extras) as Array<[ProductKind, ResolvedInstructionOption]>

  return (
    <SectionedBorderBox variant="flat"
      title={t('purchase_flow_revamp:instructions.extras.section_title')}
      description={t('purchase_flow_revamp:instructions.extras.section_description')}
    >
      {isLoading &&
        <PfInlineLoading message={t('purchase_flow_revamp:instructions.extras.loading')} />
      }

      {!isLoading && hasError &&
        <Typography variant="text-sm" color="error">
          {t('purchase_flow_revamp:instructions.extras.error')}
        </Typography>
      }

      {!isLoading && !hasError && entries.length === 0 &&
        <Typography variant="text-sm" color={GRAY_700}>
          {t('purchase_flow_revamp:instructions.extras.empty')}
        </Typography>
      }

      {!isLoading && entries.length > 0 &&
        <Stack gap={0.8}>
          {entries.map(([kind, option]) => {
            // A plan-only extra reaching this list means the plan includes it —
            // the page withholds it entirely otherwise. It reads as settled,
            // not as a choice: ticked, locked, and free.
            const included = subscriptionIncluded && SUBSCRIPTION_ONLY_EXTRA_KINDS.has(kind)
            return (
              <MUICheckCard
                key={kind}
                checked={included || selectedSet.has(kind)}
                disabledSelection={included}
                selectionStyle="ring"
                onCheck={() => onToggle(kind, !selectedSet.has(kind))}
                title={included
                  ? t('fast_delivery_instruction_card:title_subscribed')
                  : t(`purchase_flow_revamp:instructions.extras.kind.${kind}`, { defaultValue: kind })}
                description={t(`purchase_flow_revamp:instructions.extras.hint.${kind}`, { defaultValue: '' }) || undefined}
                trailing={
                  <Typography variant="text-sm" fontWeight={600} color={included ? GREEN_600 : GRAY_900}>
                    {included
                      ? t('fast_delivery_instruction_card:free_badge')
                      : formatInstructionOptionPrice(option, currency)}
                  </Typography>
                }
                data-cy={`pf-revamp-extra-${kind}`}
              />
            )
          })}
        </Stack>
      }
    </SectionedBorderBox>
  )
}
